// @ts-nocheck
/**
 * Size calculator — generic, DOM-driven boot sizing.
 *
 * Reads the chart from rendered `[data-row]` table rows inside each
 * `[data-size-calculator]` section. We use the DOM (not an inline JSON
 * payload) because Shopify's theme-blocks system exposes blocks to the
 * parent section iteration with type `@theme` and unreachable settings —
 * each block file is the only place that can read its own settings, so it
 * emits them as data-* attributes for us to harvest at runtime.
 *
 * Replaces a ~700-line legacy implementation that had four hard-coded
 * charts plus dispatch functions; the chart-type variations (whether Euro
 * size shows, whether narrow / double-wide categories exist) are driven by
 * data-* flags on the section root.
 *
 * Comments are written in Australian English per project standard.
 */
(function () {
  'use strict';

  // Initialise every calculator within `scope` (defaults to the whole
  // document), skipping any already wired up. Exposed on `window` so
  // calculators injected after page load — e.g. fetched into a modal — can be
  // initialised on demand.
  function initAll(scope) {
    (scope || document).querySelectorAll('[data-size-calculator]').forEach(function (root) {
      if (root.dataset.scInitialised === 'true') return;
      root.dataset.scInitialised = 'true';
      init(root);
    });
  }

  window.SizeCalculator = window.SizeCalculator || {};
  window.SizeCalculator.initAll = initAll;

  initAll();

  function init(root) {
    const flags = {
      euro: root.dataset.showEuro === 'true',
      width: root.dataset.showWidthInput === 'true',
      narrow: root.dataset.enableNarrow === 'true',
      doubleWide: root.dataset.enableDoubleWide === 'true',
    };

    const labels = {
      notSpecified: root.dataset.labelNotSpecified || 'Not specified',
      noMatch: root.dataset.labelNoMatch || 'Size not found',
      empty: root.dataset.labelEmpty || 'Enter measurement',
      custom: root.dataset.labelCustom || 'Custom',
      narrow: root.dataset.labelNarrow || 'Narrow',
      standard: root.dataset.labelStandard || 'Standard',
      wide: root.dataset.labelWide || 'Wide',
      doubleWide: root.dataset.labelDoubleWide || 'Double wide',
    };

    const trigger = root.querySelector('[data-calc-trigger]');
    if (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        calculate(root, flags, labels);
      });
    }

    // Enter key on any input inside the section runs the calculation too.
    root.querySelectorAll('input[type="number"]').forEach(function (input) {
      input.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          calculate(root, flags, labels);
        }
      });
    });
  }

  /**
   * Harvest the chart from rendered table rows. Done fresh on every
   * calculate() call so live block edits in the theme editor show up
   * without a full page reload.
   */
  function readRows(root) {
    return Array.prototype.map.call(
      root.querySelectorAll('[data-row]'),
      function (tr) {
        return {
          el: tr,
          min: num(tr.dataset.min),
          max: num(tr.dataset.max),
          bont: tr.dataset.bont || '',
          euro: tr.dataset.euro || '',
          std: [num(tr.dataset.standardMin), num(tr.dataset.standardMax)],
          wide: [num(tr.dataset.wideMin), num(tr.dataset.wideMax)],
          narrow: [num(tr.dataset.narrowMin), num(tr.dataset.narrowMax)],
          dw: [num(tr.dataset.dwMin), num(tr.dataset.dwMax)],
        };
      }
    );
  }

  function calculate(root, flags, labels) {
    const rows = readRows(root);

    // Clear any previously-matched row highlights before the new run.
    root.querySelectorAll('[data-row].is-matched').forEach(function (tr) {
      tr.classList.remove('is-matched');
    });

    // Reveal the results card. Toggling the attribute off → on re-triggers
    // the CSS entrance animation on every Calculate click.
    root.setAttribute('data-results-shown', 'false');
    // Force a reflow so the animation restarts even when the attribute was
    // already 'true' on the previous run.
    void root.offsetWidth;
    root.setAttribute('data-results-shown', 'true');

    ['left', 'right'].forEach(function (side) {
      const lengthInput = root.querySelector('[data-input-' + side + '-length]');
      const widthInput = root.querySelector('[data-input-' + side + '-width]');
      const lengthVal = lengthInput && lengthInput.value !== '' ? parseFloat(lengthInput.value) : NaN;
      const widthVal = widthInput && widthInput.value !== '' ? parseFloat(widthInput.value) : NaN;

      if (isNaN(lengthVal)) {
        setResult(root, side, {
          bont: labels.empty,
          euro: labels.empty,
          width: labels.notSpecified,
        });
        return;
      }

      const row = rows.find(function (r) { return lengthMatches(lengthVal, r); });
      if (!row) {
        setResult(root, side, {
          bont: labels.noMatch,
          euro: labels.noMatch,
          width: labels.notSpecified,
        });
        return;
      }

      const widthCategory = flags.width ? classifyWidth(widthVal, row, flags, labels) : labels.notSpecified;

      setResult(root, side, {
        bont: row.bont || '—',
        euro: row.euro || '—',
        width: widthCategory,
      });

      // Visual link between the result and the table: mark the matching row.
      // Both feet can match the same row or different rows; either is fine.
      if (row.el) {
        row.el.classList.add('is-matched');
      }
    });
  }

  function classifyWidth(width, row, flags, labels) {
    if (isNaN(width)) return labels.notSpecified;
    if (flags.narrow && inRange(width, row.narrow)) return labels.narrow;
    if (inRange(width, row.std)) return labels.standard;
    if (inRange(width, row.wide)) return labels.wide;
    if (flags.doubleWide && inRange(width, row.dw)) return labels.doubleWide;
    return labels.custom;
  }

  /**
   * Match against a partial or full range. Partial entries use single-point
   * semantics so they line up with the table display (which renders a row
   * with only one bound as the single value, not as an open-ended range):
   *   both set  → min <= value <= max
   *   only min  → value === min        (single point)
   *   only max  → value === max        (single point)
   *   neither   → no match (category not defined for this row)
   * "Set" means > 0 — number settings default to 0 when the merchant leaves
   * the editor field blank, so 0 is our sentinel for "unset". For an open
   * upper bound, the merchant should set max explicitly (e.g. 999).
   */
  function inRange(value, pair) {
    if (isNaN(value)) return false;
    if (!pair || pair.length !== 2) return false;
    const min = pair[0];
    const max = pair[1];
    if (min > 0 && max > 0) return value >= min && value <= max;
    if (min > 0) return value === min;
    if (max > 0) return value === max;
    return false;
  }

  /**
   * Length match uses the same single-point semantics as width for partial
   * entries. Treating only-min as "value >= min" causes the wrong row to win
   * when a later row's bounded range covers the same value, so we require
   * exact equality when only one bound is set.
   *   both set  → min <= length <= max  (typical case)
   *   only min  → length === min        (single point)
   *   only max  → length === max        (single point)
   *   neither   → no match              (row has no length range defined)
   */
  function lengthMatches(value, row) {
    if (isNaN(value)) return false;
    if (row.min > 0 && row.max > 0) return value >= row.min && value <= row.max;
    if (row.min > 0) return value === row.min;
    if (row.max > 0) return value === row.max;
    return false;
  }

  function setResult(root, side, values) {
    const bont = root.querySelector('[data-result-' + side + '-bont]');
    const euro = root.querySelector('[data-result-' + side + '-euro]');
    const width = root.querySelector('[data-result-' + side + '-width]');
    if (bont) bont.textContent = values.bont;
    if (euro) euro.textContent = values.euro;
    if (width) width.textContent = values.width;
  }

  function num(v) {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  }
})();
